import type { CoverageStatus, ReportRow, SilentRange } from '../../types/flowBilling'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const parts = (day: string) => ({ month: MONTHS[Number(day.slice(5, 7)) - 1], date: Number(day.slice(8, 10)) })

/**
 * "Aug 1–4, 7–10, Aug 31" style: the month is named once per run of ranges in
 * that month, and a single day is just the day.
 */
export function formatSilentRanges(ranges: SilentRange[]): string {
  let lastMonth: string | null = null
  return ranges
    .map(({ from, to }) => {
      const start = parts(from)
      const end = parts(to)
      const head = start.month === lastMonth ? `${start.date}` : `${start.month} ${start.date}`
      lastMonth = end.month
      if (from === to) return head
      return end.month === start.month ? `${head}–${end.date}` : `${head}–${end.month} ${end.date}`
    })
    .join(', ')
}

export const silentDayCount = (ranges: SilentRange[]) =>
  ranges.reduce((sum, r) => sum + (Date.parse(r.to) - Date.parse(r.from)) / 86_400_000 + 1, 0)

/** Word first, so the status survives without colour (greyscale, screen readers). */
export const COVERAGE_LABEL: Record<CoverageStatus, string> = {
  complete: 'Complete',
  gaps: 'Gaps',
  commissioning: 'Commissioning',
  outage: 'Outage',
}

export const COVERAGE_STYLE: Record<CoverageStatus, string> = {
  complete: 'bg-green-50 text-green-800 ring-green-600/20',
  gaps: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  // Expected, not fine: still listed, still "not in total", but not an alarm.
  commissioning: 'bg-gray-100 text-gray-700 ring-gray-500/20',
  outage: 'bg-red-50 text-red-800 ring-red-600/20',
}

/** What happened and what to do, in a sentence a billing reader can act on. */
export function coverageExplanation(row: ReportRow): string {
  const ranges = row.silentRanges ?? []
  const lost = row.missedMessages ?? 0
  const sentences: string[] = []

  const outage = ranges.filter((r) => !r.commissioning)
  const commissioning = ranges.filter((r) => r.commissioning)
  if (outage.length > 0) {
    sentences.push(`No readings ${formatSilentRanges(outage)}, with no configuration change nearby.`)
  }
  if (commissioning.length > 0) {
    sentences.push(`No readings ${formatSilentRanges(commissioning)}, beside a configuration change (installation or setup).`)
  }
  if (ranges.length > 0) {
    sentences.push(
      'Any flow on those days is not in the total and has not been estimated. Check against the meter register before billing.'
    )
  }
  if (lost > 0) {
    sentences.push(
      `${lost} message${lost === 1 ? '' : 's'} sent but never received; any volume they carried is not in the total.`
    )
  }
  return sentences.length > 0 ? sentences.join(' ') : 'Readings arrived every day and none were lost.'
}
